import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from 'react';
import { Loading, Error, useQueryWithStore, useNotify } from 'react-admin';
import { FormSpy } from 'react-final-form';
import DocumentApi from '../../../../core/apis/DocumentApi';
import ResourceUploadApi from '../../../../core/apis/ResourceUploadApi';
import dataProvider from '../../../../core/providers/IccUserDataProvider';
import EntitlementApi from '../../../../core/apis/EntitlementApi';
import { getIcError } from '../../../../core/providers/utils';
import isEqual from 'lodash/isEqual';
import useInterval from '../../../../core/hooks/UseInterval';
import SplitDocumentTable from '../SplitStep/SplitStepDocumentTable';
import SplitActionBar from '../SplitStep/SplitStepActionBar';
import useNotifyServerError from '../../../../core/hooks/useNotifyServerError';
import CustomSimpleForm from '../../../../core/components/form/CustomSimpleForm';

const MAX_COUNTER = 50; // 50 * 5 sec = 4.16 min

/** Main assign step component */
const AssignSplitStep = props => {
  const {
    resourceupload = {},
    onFormInit,
    resource,
    disableActionButtons,
    handleResetActionButtons,
    onSubmitEnd,
    setSplitStatus: setSplitStatusGlobally,
    handleResetClosingState
  } = props;
  const { resourceUploadId, categoryId, profileData = {} } = resourceupload;
  const { splitMethodId = '' } = profileData;

  const notifyServerError = useNotifyServerError();
  const formRef = useRef();
  const [splitStatus, setSplitStatus] = useState('NONE');
  const [splitStatusDescription, setSplitStatusDescription] = useState('');
  const [counter, setCounter] = useState(0);
  const [delay, setDelay] = useState(5000);
  const notify = useNotify();

  const maxCounterReached = useMemo(() => {
    return counter >= MAX_COUNTER - 1;
  }, [counter]);

  //callback used to fetch split status from service
  const getSplitStatus = useCallback(async () => {
    setCounter(count => count + 1);
    try {
      const newSplitStatus = await ResourceUploadApi.getSplitStatus(
        resourceUploadId
      );
      if (
        (newSplitStatus && newSplitStatus.status !== 'PROCESSING') ||
        maxCounterReached
      ) {
        setDelay(null);
        setCounter(0);
      }
      setSplitStatus(newSplitStatus.status);
      if (newSplitStatus.status === 'ERROR') {
        setSplitStatusDescription(newSplitStatus.statusInfo);
      }
    } catch (error) {
      setDelay(null);
      setSplitStatus('ERROR');
      notifyServerError(error);
    }
  }, [
    resourceUploadId,
    setSplitStatus,
    maxCounterReached,
    setCounter,
    notifyServerError
  ]);

  //calback used to initiate split
  const handleSplit = useCallback(
    async splitMethod => {
      setSplitStatus('PROCESSING');
      // Initialize split
      try {
        await ResourceUploadApi.split(
          resourceupload.resourceUploadId,
          splitMethod
        );
        setDelay(5000);
      } catch (error) {
        const errorMessage = getIcError(error);
        setSplitStatus('ERROR');
        setSplitStatusDescription(errorMessage);
      }
    },
    [resourceupload, setSplitStatus, setDelay]
  );

  //hook used to initiate periodically checking of split status
  useInterval(() => {
    getSplitStatus();
  }, delay);

  //effect used to set split status globally so navigation buttons are properly enabled/disabled
  useEffect(() => {
    setSplitStatusGlobally(splitStatus);
  }, [splitStatus, setSplitStatusGlobally]);

  //hook used to populate fund and investor lookups, investor without contacts and unrefferenced investors
  const { loaded, error, loading, data } = useQueryWithStore({
    type: 'getManyReference',
    resource: 'fundinvestors',
    payload: {
      target: 'fundId',
      id: resourceupload.fundIds,
      pagination: { perPage: -1 },
      options: { chunk: true }
    }
  });

  //callback used to setInvestorWithoutContactsIds
  const calculateInvestorsWithoutContactsIds = useCallback(
    async documents => {
      const matchedDocs = documents.filter(
        e => e.matchingStatus !== 'MISMATCH'
      );
      if (matchedDocs.length > 0) {
        const referencedInvestors = [];
        matchedDocs.forEach(doc => {
          referencedInvestors.push(doc.investorId);
        });
        const roleFilters = matchedDocs.map(matchedDoc => ({
          investorId: matchedDoc.investorId,
          fundId: matchedDoc.fundId,
          userRole: 'REGULAR_CONTACT',
          categoryOrSubcategoryId: categoryId
        }));
        let userAuthorizations = [];
        try {
          userAuthorizations = await EntitlementApi.getAuthorizationsByRole(
            roleFilters
          );
        } catch (error) {
          notifyServerError(error);
        }
        const investorWithoutContactsIds = referencedInvestors.filter(
          investorId => {
            return !userAuthorizations.some(authorization => {
              return (
                authorization.investorId === investorId &&
                authorization.users.length > 0
              );
            });
          }
        );
        formRef.current.change(
          'investorWithoutContactsIds',
          investorWithoutContactsIds
        );
      }
    },
    [categoryId, formRef, notifyServerError]
  );

  //#region Split Step Save Action

  /**
   * Link resourceupload to investors (Assign Split) - form submit action
   */
  const saveInvestors = useCallback(
    async values => {
      /**
       * check if documents is changed from initial
       */
      const docIsChanged = (document, initialDocuments) => {
        const initialDoc = initialDocuments.find(
          element => element.id === document.id
        );
        // compare initial and current document wihout irrelevant fields added for fitlering and selecting in grid
        const test = isEqual(
          { ...document, selected: undefined, isFilteredOut: undefined },
          { ...initialDoc, selected: undefined, isFilteredOut: undefined }
        );
        return !test;
      };

      /**
       * get all changed documents
       */
      const changedDocs = currState => {
        const changedDocs = currState.values.splittedDocuments.filter(doc =>
          docIsChanged(doc, currState.initialValues.splittedDocuments)
        );
        return changedDocs;
      };

      const splittedDocuments = values.splittedDocuments;
      disableActionButtons();
      const formState = formRef.current.getState();
      let changedDocuments = [];

      // If the form is untouched then do nothing and go to next step.
      if (formState.pristine || splittedDocuments.length === 0) {
        onSubmitEnd(resourceupload);
        return;
      } else {
        // Since extra fields are added to form and are affecting pristine property of form (only splittedDocuments are relevant)
        // adding extra condition to check if documents are changed to prevent calling of update documents
        changedDocuments = changedDocs(formState);
        if (changedDocuments && changedDocuments.length === 0) {
          onSubmitEnd(resourceupload);
          return;
        }
      }

      let documents = [];

      //call update just for changed docs
      changedDocuments.forEach(doc => {
        if (
          doc.fundId !== 'MultiFund' &&
          doc.investorId !== 'Un-Matched' &&
          doc.matchingStatus !== 'INACTIVE_INVESTOR'
        ) {
          documents.push({
            id: doc.id,
            fundId: doc.fundId,
            investorId: doc.investorId,
            documentTitle: doc.documentTitle,
            documentDate: doc.documentDate,
            documentSecurityId: doc.documentSecurityId,
            dueDate: doc.dueDate,
            watermarkId: doc.watermarkId === 0 ? null : doc.watermarkId,
            categoryId: doc.categoryId,
            subcategoryId: doc.subcategoryId
          });
        }
      });

      if (documents.length > 0) {
        try {
          // Update individual documents
          await DocumentApi.assignDocuments(resourceUploadId, documents);

          // Refresh the batch info after : status could have changed
          // Depending on Maker/Checker feature turned ON
          const { data } = await dataProvider('GET_ONE', 'resourceuploads', {
            id: resourceupload.resourceUploadId
          });

          if (onSubmitEnd) {
            onSubmitEnd(data);
          }
        } catch (error) {
          handleResetActionButtons();
          //reset closing dialog state to false in case submit was called with Save & Close
          handleResetClosingState();
          const errorMessage = getIcError(error);
          if (
            errorMessage ===
            'The document batch contains the same investor more than once which is not supported functionality. You will need to remove the multiple instance to move to the next step in the document process.'
          ) {
            notify('ra.notification.server', {
              type: 'warning',
              undoable: false,
              autoHideDuration: 30000,
              messageArgs: {
                message: errorMessage,
                isNewAdminCustomWarning: true
              }
            });
          } else {
            notifyServerError(error);
          }
        }
      } else {
        onSubmitEnd(resourceupload);
      }
    },
    [
      notifyServerError,
      resourceUploadId,
      resourceupload,
      onSubmitEnd,
      disableActionButtons,
      handleResetActionButtons,
      notify,
      handleResetClosingState
    ]
  );

  //#endregion

  if (!loaded || loading) return <Loading />;
  if (error) return <Error />;

  return (
    <CustomSimpleForm
      save={saveInvestors}
      toolbar={null}
      resource={resource}
      initialValues={{
        splittedDocuments: [],
        selectedFilter: 'SHOW_ALL',
        reSplitMethod: splitMethodId,
        investorWithoutContactsIds: []
      }}
      variant="standard"
      margin="normal"
      initialValuesEqual={() => true}
    >
      <FormSpy subscription={{ values: true, pristine: true }}>
        {formprops => {
          formRef.current = formprops.form;
          if (onFormInit !== undefined) {
            onFormInit(formprops.form);
          }
          const {
            splittedDocuments = [],
            reSplitMethod
          } = formprops.form.getState().values;

          const selectedRows = splittedDocuments.reduce(
            (selectedIds, document) => {
              if (document.selected && !document.isFilteredOut) {
                selectedIds.push(document.id);
              }
              return selectedIds;
            },
            []
          );

          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%'
              }}
            >
              <SplitDocumentTable
                splittedDocuments={splittedDocuments}
                resource={resource}
                resourceupload={resourceupload}
                handleSplit={() => handleSplit(reSplitMethod)}
                splitStatus={splitStatus}
                selectedRows={selectedRows}
                fundInvestors={data}
                splitStatusDescription={splitStatusDescription}
                setDelay={setDelay}
                setSplitStatus={setSplitStatus}
                splitMethod={reSplitMethod}
                calculateInvestorsWithoutContactsIds={
                  calculateInvestorsWithoutContactsIds
                }
              />
              <SplitActionBar
                resource={resource}
                splitStatus={splitStatus}
                resourceupload={resourceupload}
                selectedRows={selectedRows}
                splittedDocuments={splittedDocuments}
                handleSplit={() => handleSplit(reSplitMethod)}
                calculateInvestorsWithoutContactsIds={
                  calculateInvestorsWithoutContactsIds
                }
              />
            </div>
          );
        }}
      </FormSpy>
    </CustomSimpleForm>
  );
};
export default AssignSplitStep;
